import React, { useMemo, useRef, useEffect } from 'react';
import { View, Pressable, Keyboard, Platform, StyleSheet, Text as RNText, type ViewStyle } from 'react-native';
import { loadSchemaFormStyles } from '@utils/loadWebStyles';

const webClass = (className: string) =>
  Platform.OS === 'web' ? ({ className } as Record<string, string>) : {};

const WEB_FIELD_ITEM_GAP = 24;
import { FastField, Field, FieldProps } from 'formik';
import AnimatedLottieView from '@utils/lottieAnimation';
import HandleFieldType from './handleFieldType';
import ErrorBoundary from '@utils/ErrorBoundary';
import { Label, Text, Separator, YStack, XStack } from 'tamagui';
import parseByDelimiter from '../utils/parseByDelimiter';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import HelpText from './handleFieldType/shared/HelpText';
import { getHelpText } from './handleFieldType/shared/fieldUtils';
import ErrorMessage from './handleFieldType/shared/ErrorMessage';

export const GetField = React.memo(({ blackText, hideLabel, otherVals, item, field, allItems, index, nextFieldFunction, setInputRef, fieldProps, readOnly, zIndex, inlineFields, insideBottomSheet, stackedColumn, delegateScrollToParent, fieldStyle, labelStyle }) => {
    useEffect(() => {
      if (Platform.OS === 'web') loadSchemaFormStyles();
    }, []);
    const theme = themeSelector();
    const hasError = !!fieldProps?.meta?.error && !!fieldProps?.meta?.touched;
    // Use stable dependencies - don't include 'field' as it changes on every keystroke
    // Only include item and fieldProps.meta for error/touched state
    
    // Check if this is a dropdown field
    const DROPDOWN_TYPES = [
      'select', 'picker', 'dropdown', 'dropDown', 'combobox',
      'template_select', 'whatsapp_template_select',
      'date', 'time', 'datetime', 'user_select', 'user_stores',
      'userSelect', 'userStores', 'calendar_select', 'calendar', 'calendars', 'populate.calendarList',
      'timezone', 'timeZone', 'time_zone', 'time zone'
    ];
    const refOrKey = String(item.ref || item.key || '');
    const refOrKeyNormalized = refOrKey.toLowerCase();
    const isDropdown = DROPDOWN_TYPES.includes(item.type) || 
                       DROPDOWN_TYPES.includes(item.content?.type) ||
                       DROPDOWN_TYPES.includes(item.ref) ||
                       DROPDOWN_TYPES.includes(item.key) ||
                       (refOrKey && (
                         refOrKeyNormalized.includes('user') || 
                         refOrKeyNormalized.includes('select') || 
                         refOrKeyNormalized.includes('dropdown') ||
                         refOrKeyNormalized.includes('stores') ||
                         refOrKeyNormalized.includes('timezone') ||
                         refOrKeyNormalized.includes('time_zone') ||
                         refOrKeyNormalized.includes('time zone')
                       ));

    const isWeb = Platform.OS === 'web';
    const isBooleanLike =
      ['boolean', 'switch', 'checkbox'].includes(String(item.type || '').toLowerCase()) ||
      ['boolean', 'switch', 'checkbox'].includes(String(item.content?.type || '').toLowerCase());
    const showFieldLabel =
      !hideLabel &&
      item.title !== 'avatar' &&
      item.content?.type !== 'avatar' &&
      !isBooleanLike;

    const labelColor = hasError
      ? '#ef4444'
      : (labelStyle?.color || ((blackText ?? theme) ? 'rgb(51,51,51)' : '#e2e8f0'));
    const labelText = item.content?.label || item.label || parseByDelimiter(item.title);

    const requiredIndicator = item.content?.validation?.required ? (
      <View style={{ maxWidth: 40, backgroundColor: 'transparent', flexShrink: 0 }}>
        {fieldProps?.meta?.touched && !fieldProps.meta.error ? (
          <AnimatedLottieView
            source={require('@assets/animations/registerSuccess.json')}
            style={{ flex: 1, width: 32, height: 32 }}
            autoPlay
          />
        ) : (
          <AnimatedLottieView
            colorFilters={[{ keypath: 'layers.1.shapes.0.it.1.c.k', color: 'red' }]}
            source={require('@assets/animations/exclamation-mark.json')}
            style={{ flex: 1, width: 32, height: 32 }}
            autoPlay
          />
        )}
      </View>
    ) : null;
    
    const needsWebStackingContext = isWeb && (isDropdown || item.type === 'phone');
    const fieldShellStyle: ViewStyle | undefined =
      needsWebStackingContext
        ? { position: 'relative', zIndex: zIndex || (isDropdown ? 50000 : undefined) }
        : !isWeb && (zIndex || isDropdown)
          ? { position: 'relative', zIndex: zIndex || (isDropdown ? 50000 : undefined) }
          : undefined;

    const fieldBody = (
      <>
        {(requiredIndicator || showFieldLabel) && (
          <View
            {...webClass('ss-schema-form-field-label-row')}
            style={
              isWeb
                ? {
                    width: '100%',
                    flexShrink: 0,
                    flexGrow: 0,
                    marginBottom: 6,
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                  }
                : undefined
            }
          >
            {requiredIndicator ? (
              <View style={{ backgroundColor: 'transparent', width: 36, flexShrink: 0 }}>
                {requiredIndicator}
              </View>
            ) : null}
            {showFieldLabel ? (
              isWeb ? (
                <RNText
                  {...webClass('ss-schema-form-field-label')}
                  style={[
                    fieldStyles.webLabelStyle,
                    labelStyle,
                    { color: labelColor, flex: 1, minWidth: 0 },
                  ]}
                >
                  {labelText}
                </RNText>
              ) : (
                <Text
                  flexWrap="wrap"
                  textWrap="wrap"
                  style={[fieldStyles.typeStyle, labelStyle, { color: labelColor }]}
                >
                  {labelText}
                </Text>
              )
            ) : null}
          </View>
        )}
        <ErrorMessage error={fieldProps?.meta?.error as string} visible={hasError} />
        <View
          {...webClass('ss-schema-form-field-control')}
          style={
            isWeb
              ? { width: '100%', flexShrink: 0, flexGrow: 0, alignSelf: 'stretch', overflow: 'visible' }
              : undefined
          }
        >
          <HandleFieldType
            blackText={blackText}
            setRef={(fieldName, ref) => setInputRef?.(fieldName, ref)}
            nextFieldFunction={(index, length) => {
              nextFieldFunction(
                allItems[index + 1]?.key || allItems[index + 1]?.title,
                index,
                length
              );
            }}
            otherVals={otherVals}
            index={index}
            fieldProps={field}
            schemaItem={item}
            length={allItems?.length}
            readOnly={readOnly}
            inlineFields={inlineFields}
            insideBottomSheet={insideBottomSheet}
            delegateScrollToParent={delegateScrollToParent}
            customStyle={fieldStyle}
            zIndex={zIndex}
          />
        </View>
        {/* Native only — web handleFieldType renders help text below the control */}
        {!isWeb ? <HelpText text={getHelpText(item)} theme={theme} /> : null}
      </>
    );

    if (isWeb) {
      return (
        <View
          {...webClass('ss-schema-form-field')}
          style={[
            fieldShellStyle,
            {
              width: '100%',
              flexShrink: 0,
              flexGrow: 0,
              alignSelf: 'stretch',
              marginBottom: stackedColumn ? 0 : 16,
              overflow: 'visible',
            },
          ]}
        >
          {fieldBody}
        </View>
      );
    }

    return (
      <YStack
        minHeight={80}
        flex={stackedColumn ? 0 : 1}
        width="100%"
        maxWidth="100%"
        minWidth={0}
        alignSelf="stretch"
        zIndex={zIndex || (isDropdown ? 50000 : undefined)}
        style={fieldShellStyle}
      >
        {(requiredIndicator || showFieldLabel) && (
          stackedColumn ? (
            <View style={{ width: '100%', minWidth: 0, marginBottom: 5 }}>
              {requiredIndicator ? (
                <View style={{ backgroundColor: 'transparent', width: 36, flexShrink: 0, marginBottom: 4 }}>
                  {requiredIndicator}
                </View>
              ) : null}
              {showFieldLabel ? (
                <Text
                  flexWrap="wrap"
                  textWrap="wrap"
                  style={[
                    fieldStyles.typeStyle,
                    fieldStyles.stackedLabelStyle,
                    labelStyle,
                    { color: labelColor },
                  ]}
                >
                  {labelText}
                </Text>
              ) : null}
            </View>
          ) : (
            <XStack flex={1} width="100%" minWidth={0} alignItems="flex-start">
              <View style={{ backgroundColor: 'transparent', maxWidth: 60, top: -5, flexShrink: 0 }}>
                {requiredIndicator}
              </View>
              {showFieldLabel && (
                <YStack marginBottom={5} flex={1} minWidth={0}>
                  <Text
                    flexWrap="wrap"
                    textWrap="wrap"
                    style={[fieldStyles.typeStyle, labelStyle, { color: labelColor }]}
                  >
                    {labelText}
                  </Text>
                </YStack>
              )}
            </XStack>
          )
        )}
        <ErrorMessage error={fieldProps?.meta?.error as string} visible={hasError} />
        <HandleFieldType
          blackText={blackText}
          setRef={(fieldName, ref) => setInputRef?.(fieldName, ref)}
          nextFieldFunction={(index, length) => {
            nextFieldFunction(
              allItems[index + 1]?.key || allItems[index + 1]?.title,
              index,
              length
            );
          }}
          otherVals={otherVals}
          index={index}
          fieldProps={field}
          schemaItem={item}
          length={allItems?.length}
          readOnly={readOnly}
          inlineFields={inlineFields}
          insideBottomSheet={insideBottomSheet}
          delegateScrollToParent={delegateScrollToParent}
          customStyle={fieldStyle}
          zIndex={zIndex}
        />
        <HelpText text={getHelpText(item)} theme={theme} />
      </YStack>
    );
  }, (prevProps, nextProps) => {
    // Re-render when the field value changes (required for selects + conditional visibility).
    const prevVal = prevProps.fieldProps?.field?.value;
    const nextVal = nextProps.fieldProps?.field?.value;
    const valueUnchanged =
      prevVal === nextVal ||
      (prevVal != null &&
        nextVal != null &&
        typeof prevVal === 'object' &&
        typeof nextVal === 'object' &&
        JSON.stringify(prevVal) === JSON.stringify(nextVal));

    return (
      valueUnchanged &&
      prevProps.item?.title === nextProps.item?.title &&
      prevProps.item?.key === nextProps.item?.key &&
      prevProps.item?.type === nextProps.item?.type &&
      prevProps.fieldProps?.meta?.touched === nextProps.fieldProps?.meta?.touched &&
      prevProps.fieldProps?.meta?.error === nextProps.fieldProps?.meta?.error &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.hideLabel === nextProps.hideLabel &&
      prevProps.blackText === nextProps.blackText &&
      prevProps.stackedColumn === nextProps.stackedColumn &&
      prevProps.delegateScrollToParent === nextProps.delegateScrollToParent
    );
  });

const Row = React.memo(({ blackText, otherVals, items, allItems, index: rowIndex, hideLabel, nextFieldFunction, setInputRef, column, totalRowsCount, readOnly = false, inlineFields = false, insideBottomSheet = false, delegateScrollToParent = false, fieldStyle, labelStyle }) => {
  const totalItemsInRow = items.length;

    const isWeb = Platform.OS === 'web';
    const isWebColumnStack = isWeb && column;
    const useWebRowStacking = isWebColumnStack && totalItemsInRow > 1;

    return (
      <View
        {...(isWeb
          ? webClass(column ? 'ss-schema-form-row' : 'ss-schema-form-row ss-schema-form-row--grid')
          : {})}
        style={{
        // Column layouts stack fields vertically — flex:1 would stretch the row to the
        // ScrollView viewport and split space between fields (huge gaps). Grid rows keep flex:1.
        flex: isWeb ? 0 : (column ? 0 : 1),
        flexShrink: isWeb ? 0 : undefined,
        flexGrow: isWeb ? 0 : undefined,
        flexDirection: column ? 'column' : 'row',
        minWidth: 0,
        maxWidth: '100%',
        alignSelf: "stretch",
        width: '100%',
        alignItems: isWebColumnStack ? 'stretch' : undefined,
        gap: isWebColumnStack ? WEB_FIELD_ITEM_GAP : (isWeb ? 25 : (column ? 20 : 25)),
        padding: isWebColumnStack && totalItemsInRow === 1 ? 0 : 5,
        backgroundColor: "transparent",
        // Only multi-field rows need z-index stacking (dropdown menus over fields below).
        zIndex: useWebRowStacking && totalRowsCount !== undefined ? (totalRowsCount - rowIndex) : undefined,
        position: useWebRowStacking ? 'relative' : undefined,
      }}>
        {items.map((item, index) => { // 'index' here is the index of the item within the current row
          // Base zIndex for item within the row: Earlier items (lower index, leftmost) get higher zIndex.
          // This makes sure a dropdown on the left appears over fields to its right within the same row.
          let itemZIndex = totalItemsInRow - index;

          // CRITICAL: Identify all dropdown types here.
          // This includes standard dropdowns and user selection dropdowns.
          const DROPDOWN_TYPES = [
            'select', 
            'picker', 
            'dropdown', 
            'dropDown',
            'combobox',
            'template_select',
            'whatsapp_template_select',
            'date', 
            'calendar_select',
            'calendar',
            'calendars',
            'populate.calendarList',
            'time', 
            'datetime',
            'user_select',
            'user_stores',
            'userSelect',
            'userStores',
            'timezone',
            'timeZone',
            'time_zone',
            'time zone'
          ];
          
          // Check if item type is a dropdown
          const isDropdownType = DROPDOWN_TYPES.includes(item.type) || 
                                 DROPDOWN_TYPES.includes(item.content?.type) ||
                                 DROPDOWN_TYPES.includes(item.ref) ||
                                 DROPDOWN_TYPES.includes(item.key);
          
          // Check if ref/key indicates a dropdown (common pattern for user selection fields)
          const refOrKey = String(item.ref || item.key || '');
          const refOrKeyNormalized = refOrKey.toLowerCase();
          const isDropdownByRef = refOrKey && (
            refOrKeyNormalized.includes('user') || 
            refOrKeyNormalized.includes('select') || 
            refOrKeyNormalized.includes('dropdown') ||
            refOrKeyNormalized.includes('stores') ||
            refOrKeyNormalized.includes('timezone') ||
            refOrKeyNormalized.includes('time_zone') ||
            refOrKeyNormalized.includes('time zone')
          );

          const isDropdown = isDropdownType || isDropdownByRef;

          let dynamicZIndex: number | undefined = itemZIndex; // Default zIndex based on position in row

          if (isDropdown) {
            // For dropdowns, apply a very high base zIndex so menus escape normal document flow.
            // Earlier *rows* (lower rowIndex) get a larger rowOffset so upper rows stack above lower rows.
            const baseDropdownZIndex = 50000;
            const rowOffset = (totalRowsCount !== undefined ? (totalRowsCount - rowIndex) : 0) * 1000;
            // Within one row: earlier items (lower index — higher on screen in column layouts, left in row layouts)
            // must stack *above* later items so an open menu from above overlaps fields below.
            const reverseOrderOffset = totalItemsInRow - 1 - index;
            dynamicZIndex = baseDropdownZIndex + rowOffset + reverseOrderOffset;
          } else if (Platform.OS === 'web' && item.type === 'phone') {
            dynamicZIndex = Math.max(dynamicZIndex, 1000);
          } else if (Platform.OS === 'web' && column) {
            // Plain text/number fields must stay in document flow — z-index caused overlap in narrow panels.
            dynamicZIndex = undefined;
          }

          const webFieldItemStyle: ViewStyle = isWebColumnStack
            ? {
              flex: 0,
              flexShrink: 0,
              flexGrow: 0,
              width: '100%',
              alignSelf: 'stretch',
              overflow: 'visible',
              backgroundColor: 'transparent',
              ...(dynamicZIndex != null ? { position: 'relative' as const, zIndex: dynamicZIndex } : {}),
            }
            : {
              flex: 0,
              flexShrink: 0,
              flexGrow: 0,
              width: '100%',
              alignSelf: 'stretch',
              zIndex: dynamicZIndex,
              position: (item.type === 'phone' || isDropdown) ? 'relative' : undefined,
              overflow: 'visible',
              backgroundColor: 'transparent',
            };

          if (item.content?.reliesOn) {
            const fieldName = item.key || item.title;
            const htmlId = item.htmlId || fieldName;
            return (
              <Field key={htmlId} id={htmlId} name={fieldName}>
                {(fieldProps: FieldProps) => {
                  if (fieldProps.form.values[item.content.reliesOn] === item.content.reliesOnValue) {
                    return (
                      <Pressable
                        style={{
                          // Apply the calculated zIndex here
                          zIndex: dynamicZIndex,
                          borderWidth: 2,
                          alignSelf: "stretch",
                          flexDirection: "row",
                          flex: column ? 0 : 1,
                          gap: 10,
                          position: 'relative', // Ensure position is set for zIndex to work
                        }}
                        disabled={Platform.OS === 'web'}
                        onPress={() => Keyboard.dismiss()}
                        hitSlop={10}
                      >
                        <View style={{ backgroundColor: "transparent", maxWidth: 60, top: 0 }}>
                          {
                            item.content?.validation?.required &&
                            <View style={{ maxWidth: 40, backgroundColor: "transparent" }}>
                              {
                                fieldProps.meta.touched && !fieldProps.meta.error ?
                                  <AnimatedLottieView source={require("@assets/animations/registerSuccess.json")} style={{ flex: 1 }} autoPlay />
                                  :
                                  <AnimatedLottieView colorFilters={[{
                                    keypath: "layers.1.shapes.0.it.1.c.k", color: "red"
                                  }]} source={require("@assets/animations/exclamation-mark.json")} style={{ flex: 1 }} autoPlay />
                              }
                            </View>
                          }
                        </View>
                        {
                          !hideLabel && !Platform.OS === 'web' &&
                          <Label style={fieldStyles.typeStyle} htmlFor={htmlId} color={blackText ? 'black' : 'white'}>{item.content?.label || item.label || parseByDelimiter(item.title)}</Label>
                        }
                        <View style={{
                          flex: Platform.OS === 'web' || column ? undefined : 1,
                          paddingLeft: 20,
                          backgroundColor: "transparent",
                          overflow: (Platform.OS === 'web' || isDropdown) ? 'visible' : 'hidden',
                          position: (Platform.OS === 'web' || isDropdown) ? 'relative' : undefined
                        }}>
                          <ErrorBoundary>
                            <GetField hideLabel={hideLabel} blackText={blackText} otherVals={otherVals} item={item} field={fieldProps.field} allItems={allItems} index={index} nextFieldFunction={nextFieldFunction} setInputRef={setInputRef} fieldProps={fieldProps} readOnly={readOnly} zIndex={dynamicZIndex} inlineFields={inlineFields} insideBottomSheet={insideBottomSheet} stackedColumn={column} delegateScrollToParent={delegateScrollToParent} fieldStyle={fieldStyle} labelStyle={labelStyle} />
                            {
                              item.content?.parent && <Separator />
                            }
                          </ErrorBoundary>
                        </View>
                      </Pressable>
                    )

                  }
                }
                }
              </Field>
            )
          }
          if (item.type === "timeslider") {
            const fieldName = item.key || item.title;
            const htmlId = item.htmlId || fieldName;
            return (
              <FastField key={htmlId} name={fieldName}>
                {(fieldProps: FieldProps) => {
                  const fieldContent = (
                    <ErrorBoundary>
                      <View style={{
                        zIndex: dynamicZIndex,
                        position: 'relative',
                        width: '100%',
                      }}>
                        <GetField hideLabel={hideLabel} blackText={blackText} otherVals={otherVals} item={item} field={fieldProps.field} allItems={allItems} index={index} nextFieldFunction={nextFieldFunction} setInputRef={setInputRef} fieldProps={fieldProps} readOnly={readOnly} zIndex={dynamicZIndex} inlineFields={inlineFields} insideBottomSheet={insideBottomSheet} stackedColumn={column} delegateScrollToParent={delegateScrollToParent} fieldStyle={fieldStyle} labelStyle={labelStyle} />
                        {
                          item.content?.parent && <Separator />
                        }
                      </View>
                    </ErrorBoundary>
                  );

                  return Platform.OS === 'web' ? (
                    <View
                      {...webClass('ss-schema-form-field-item')}
                      style={webFieldItemStyle}
                    >
                      {fieldContent}
                    </View>
                  ) : fieldContent;
                }}
              </FastField>
            )
          }
          else {
            const fieldName = item.key || item.title;
            const htmlId = item.htmlId || fieldName;
            return (
              <FastField key={htmlId} name={fieldName}>
                {(fieldProps: FieldProps) => {
                  return Platform.OS === 'web' ? (
                    <View
                      {...webClass('ss-schema-form-field-item')}
                      style={webFieldItemStyle}
                    >
                      <ErrorBoundary>
                        <GetField fieldProps={fieldProps} hideLabel={hideLabel} blackText={blackText} otherVals={otherVals} item={item} field={fieldProps.field} allItems={allItems} index={index} nextFieldFunction={nextFieldFunction} setInputRef={setInputRef} readOnly={readOnly} zIndex={dynamicZIndex} inlineFields={inlineFields} insideBottomSheet={insideBottomSheet} stackedColumn={column} delegateScrollToParent={delegateScrollToParent} fieldStyle={fieldStyle} labelStyle={labelStyle} />
                        {
                          item.content?.parent && <Separator />
                        }
                      </ErrorBoundary>
                    </View>
                  ) :
                    (
                      (
                        <Pressable
                          style={{
                            alignSelf: "stretch",
                            width: '100%',
                            minWidth: 0,
                            maxWidth: '100%',
                            flexDirection: column ? 'column' : 'row',
                            gap: 10,
                            zIndex: dynamicZIndex,
                            position: 'relative',
                            overflow: isDropdown ? 'visible' : 'hidden',
                          }}
                          disabled={item.type === "timeslider"}
                          onPress={() => Keyboard.dismiss()}
                          hitSlop={10}
                        >
                          <View style={{ position: "absolute", backgroundColor: "transparent", minWidth: "10%", maxWidth: 60, left: -10, top: 50 }}>
                            {
                              item.content?.validation?.required &&
                              <View style={{ maxWidth: 40, backgroundColor: "transparent" }}>
                                {
                                  fieldProps.meta.touched && !fieldProps.meta.error ?
                                    <AnimatedLottieView source={require("@assets/animations/registerSuccess.json")} style={{ flex: 1 }} autoPlay />
                                    :
                                    <AnimatedLottieView colorFilters={[{
                                      keypath: "layers.1.shapes.0.it.1.c.k", color: "red"
                                    }]} source={require("@assets/animations/exclamation-mark.json")} style={{ flex: 1 }} autoPlay />
                                }
                              </View>
                            }
                          </View>

                          <View style={{
                            flex: 1,
                            width: '100%',
                            minWidth: 0,
                            maxWidth: '100%',
                            alignSelf: 'stretch',
                            backgroundColor: "transparent",
                            overflow: (Platform.OS === 'web' || isDropdown) ? 'visible' : 'hidden',
                            position: (Platform.OS === 'web' || isDropdown) ? 'relative' : undefined,
                            zIndex: isDropdown ? 500 : undefined
                          }}>
                            <ErrorBoundary>
                              <GetField
                                hideLabel={hideLabel}
                                blackText={blackText}
                                otherVals={otherVals}
                                item={item}
                                field={fieldProps.field}
                                allItems={allItems}
                                index={index}
                                nextFieldFunction={nextFieldFunction}
                                setInputRef={setInputRef}
                                readOnly={readOnly}
                                zIndex={dynamicZIndex}
                                inlineFields={inlineFields}
                                insideBottomSheet={insideBottomSheet}
                                stackedColumn={column}
                                delegateScrollToParent={delegateScrollToParent}
                                fieldStyle={fieldStyle}
                                labelStyle={labelStyle}
                              />
                              {
                                item.content?.parent && <Separator />
                              }
                            </ErrorBoundary>
                          </View>
                        </Pressable>
                      )
                    )

                }}
              </FastField>
            )
          }
        })}
      </View>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders when items array reference changes but content is the same
    // Only re-render if actual item structure changes
    if (prevProps.items?.length !== nextProps.items?.length) return false;
    if (prevProps.rowIndex !== nextProps.rowIndex) return false;
    if (prevProps.totalRowsCount !== nextProps.totalRowsCount) return false;
    if (prevProps.readOnly !== nextProps.readOnly) return false;
    if (prevProps.hideLabel !== nextProps.hideLabel) return false;
    if (prevProps.blackText !== nextProps.blackText) return false;
    if (prevProps.column !== nextProps.column) return false;
    if (prevProps.insideBottomSheet !== nextProps.insideBottomSheet) return false;
    if (prevProps.delegateScrollToParent !== nextProps.delegateScrollToParent) return false;
    
    // Check if items have actually changed by comparing keys/titles
    const prevKeys = prevProps.items?.map(i => i.key || i.title).join(',') || '';
    const nextKeys = nextProps.items?.map(i => i.key || i.title).join(',') || '';
    if (prevKeys !== nextKeys) return false;
    
    return true; // Props are equal, skip re-render
  });

const fieldStyles = StyleSheet.create({
  typeStyle: {
    fontWeight: 'bold',
    paddingHorizontal: 15,
    height: 30,
    maxHeight: 30,
    left: -10,
    justifyContent: 'center',
    color: 'rgb(51,51,51)',
    top: 10,
  },
  webLabelStyle: {
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 20,
    paddingHorizontal: 4,
    paddingVertical: 2,
    flexShrink: 0,
    alignSelf: 'stretch',
    color: 'rgb(51,51,51)',
  },
  stackedLabelStyle: {
    left: 0,
    paddingHorizontal: 0,
    height: undefined,
    maxHeight: undefined,
    top: 0,
  },
});

export default Row;